import { MAX_ENTRIES_FOR_EDIT } from 'features/drilldown/panels/global/bulkEdit/constants'
import { EntryWithEdits } from 'features/drilldown/panels/global/bulkEdit/types'
import {
  getNewValue,
  hasValueChanged,
} from 'features/drilldown/panels/global/bulkEdit/utils'
import {
  useFilteredEntryCount,
  useFilteredIds,
} from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import { useFilteredEntries } from 'features/drilldown/rolodex/dataHelpers/useFilteredEntries'
import { useMemo } from 'react'

interface UseBulkEditDataProps {
  selectedType: string
  propertyKey: string | null
  editedValues: Record<string, unknown>
  shouldPrefillValues: boolean
  prefillValue: string
}

export const useBulkEditData = ({
  selectedType,
  propertyKey,
  editedValues,
  shouldPrefillValues,
  prefillValue,
}: UseBulkEditDataProps) => {
  const { ids: filteredIds, isLoading: isLoadingIds } = useFilteredIds(
    selectedType,
    MAX_ENTRIES_FOR_EDIT
  )

  const {
    entries: filteredEntries,
    isLoading: isLoadingEntries,
    forceRefresh,
  } = useFilteredEntries(selectedType, MAX_ENTRIES_FOR_EDIT)

  const { count: totalEntryCount, isLoading: isLoadingCount } =
    useFilteredEntryCount(selectedType)

  const isWithinEntryCountLimit = totalEntryCount <= MAX_ENTRIES_FOR_EDIT

  const relevantEntries = useMemo(() => {
    if (!filteredEntries?.length || !propertyKey) {
      return []
    }

    return filteredEntries
      .filter((entry) => entry.type === selectedType)
      .slice(0, MAX_ENTRIES_FOR_EDIT)
  }, [filteredEntries, selectedType, propertyKey])

  const entriesWithEdits: ReadonlyArray<EntryWithEdits> = useMemo(() => {
    if (!propertyKey) return []

    return relevantEntries
      .map((entry) => {
        const currentValue = entry.data[propertyKey] || ''
        const newValue = getNewValue(
          entry.id,
          editedValues,
          shouldPrefillValues,
          prefillValue,
          currentValue
        )
        const isEdited = editedValues[entry.id] !== undefined
        const hasDifference = hasValueChanged(newValue, currentValue)
        return {
          key: entry.id,
          id: entry.id,
          currentValue,
          newValue,
          isEdited,
          hasDifference,
          entryData: entry.data,
        }
      })
      .sort((a, b) => a.id.localeCompare(b.id))
  }, [
    relevantEntries,
    propertyKey,
    editedValues,
    shouldPrefillValues,
    prefillValue,
  ])

  const numEntriesToUpdate = useMemo(
    () => entriesWithEdits.filter((row) => row.hasDifference).length,
    [entriesWithEdits]
  )

  return {
    filteredIds,
    totalCount: totalEntryCount,
    isLoadingIds,
    isLoadingCount,
    isLoadingEntries,
    isWithinEntryCountLimit,
    forceRefresh,
    entriesWithEdits,
    numEntriesToUpdate,
  }
}
